// src/redux/FoodOrderSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  FieldPath,
} from "firebase/firestore";
import { db } from "../firebase";
import { AppDispatch } from "./store";

/* ═══════════════════════════════════════════════════════════
   FOOD ORDERS SLICE
   Mirrors storeOrdersSlice: one thunk per status transition so the
   UI dispatches intent instead of hand-rolling updateDoc calls, a
   host-scoped query driven off hostProfits[hostId] (no full-collection
   scan), and the four brand-insight listeners bridged through the
   FastFood catalogue.

   Food orders differ from store orders in one way that matters here:
   there is no brands[] wrapper. items[] is flat and every line already
   carries hostId + listingId, so "the seller's rows" is a filter on
   items[] rather than a brand row lookup. Where the store slice reads
   raw.brands, this slice DERIVES the equivalent rows from items[] —
   see brandRowsOf — so seller screens get the same shape to render.
═══════════════════════════════════════════════════════════ */

const FOOD_ORDERS_COLLECTION = "FoodOrders";
const FOOD_COLLECTION = "FastFood";
const DAY = 86_400_000;

// ---------------------- //
// TYPES
// ---------------------- //
export interface FoodDeliveryStatus {
  sellerDelivered: boolean;
  orderDelivered: boolean;
  buyerReceived: boolean;
}

export interface FoodOrderItem {
  listingId: string;
  hostId: string;
  brandId?: string;
  qty: number;
  rate: number;
  portionId: string | null;
  extraIds: string[];
  deliveryStatus: FoodDeliveryStatus;
  payoutProcessed?: boolean;
  payoutProcessedAt?: any;
  [key: string]: any;
}

/** Derived counterpart to storeOrders' raw brands[] row. */
export interface FoodBrandRow {
  brandId: string;
  hostId: string;
  items: FoodOrderItem[];
  qty: number;
  saleTotal: number;
  deliveryStatus: FoodDeliveryStatus;
}

export interface FoodOrderCustomer {
  firstName: string;
  lastName: string;
  phone: string;
  city: string;
  address: string;
  country: string;
}

export interface HostProfit {
  saleTotal: number;
  hostsFee: number;
  deliveryFee: number;
  hostProfit: number;
}

export interface FoodOrder {
  id: string;
  guestId: string;
  items: FoodOrderItem[];
  myBrands?: FoodBrandRow[];
  subtotal: number;
  totalQty: number;
  totalPaid: number;
  deliveryTotal: number;
  guestsFee: number;
  hostsFee: number;
  hostProfits: Record<string, HostProfit>;
  platformProfit: number;
  paymentId: string;
  paymentIntentId?: string;
  fulfillmentMethod?: "FBS" | "FBP" | "FBA";
  deliveredBy?: Record<string, any>;
  customer?: FoodOrderCustomer;
  isConfirmed: boolean;
  isPaid: boolean;
  isCancelled: boolean;
  isRefunded: boolean;
  isProcessedForPayout: boolean;
  isDelivered: boolean;
  deliveryStatus: FoodDeliveryStatus;
  grossPaid?: number;
  vatCollected?: number;
  netStripeAmount?: number;
  createdAt?: any;
  updatedAt?: any;
  [key: string]: any;
}

export interface BrandCustomer {
  guestId: string;
  orders: number;
  units: number;
  totalSpent: number;
  firstOrderAt: number | null;
  lastOrderAt: number | null;
  status: "active" | "lapsed";
}

export interface TopProduct {
  listingId: string;
  name: string;
  photo?: string;
  units: number;
  revenue: number;
  orders: number;
}

// ---------------------- //
// SHARED HELPERS
// ---------------------- //

/* order-level flags are a rollup of the lines; the length check stops
   an empty items[] reading as vacuously delivered */
const rollupDelivery = (items: any[]): FoodDeliveryStatus => ({
  sellerDelivered:
    items.length > 0 && items.every((i) => i.deliveryStatus?.sellerDelivered === true),
  orderDelivered:
    items.length > 0 && items.every((i) => i.deliveryStatus?.orderDelivered === true),
  buyerReceived:
    items.length > 0 && items.every((i) => i.deliveryStatus?.buyerReceived === true),
});

/* an order counts as delivered only once every line has all three flags —
   same rule storeOrdersSlice uses for brand rows */
const isFullyDelivered = (s: FoodDeliveryStatus) =>
  s.sellerDelivered && s.orderDelivered && s.buyerReceived;

const statusPatchFor = (role: "seller" | "buyer") =>
  role === "buyer"
    ? { buyerReceived: true }
    : { sellerDelivered: true, orderDelivered: true };

/* a line's brand, falling back to hostId for legacy rows written before
   brandId was stamped on each item */
const brandKeyOf = (i: any) => String(i?.brandId ?? i?.hostId ?? "");

const lineValue = (i: any) =>
  Number(i?.lineTotal ?? Number(i?.rate ?? 0) * Number(i?.qty ?? 0));

/** Group flat items[] into the brand rows storeOrders gets for free from
    raw.brands. Delivery status per row is a rollup of its own lines, so a
    seller screen can render one delivered button per kitchen and pass the
    brandIds straight into markFoodItemsDelivered. */
export const brandRowsOf = (items: any[] = [], hostId?: string): FoodBrandRow[] => {
  const map = new Map<string, FoodBrandRow>();

  items.forEach((i) => {
    if (hostId && i.hostId !== hostId) return;
    const brandId = brandKeyOf(i);
    if (!brandId) return;

    const row =
      map.get(brandId) ??
      ({
        brandId,
        hostId: String(i.hostId ?? ""),
        items: [],
        qty: 0,
        saleTotal: 0,
        deliveryStatus: {
          sellerDelivered: false,
          orderDelivered: false,
          buyerReceived: false,
        },
      } as FoodBrandRow);

    row.items.push(i);
    row.qty += Number(i.qty ?? 0);
    row.saleTotal = Number((row.saleTotal + lineValue(i)).toFixed(2));
    map.set(brandId, row);
  });

  return [...map.values()].map((r) => ({ ...r, deliveryStatus: rollupDelivery(r.items) }));
};

/** Every line carries all three flags from the moment it's written, so
    there is always a field to patch and never an undefined to read. */
const withDeliveryDefaults = (items: any[] = []) =>
  items.map((i) => ({
    ...i,
    portionId: i?.portionId ?? null,
    extraIds: Array.isArray(i?.extraIds) ? i.extraIds : [],
    deliveryStatus: {
      sellerDelivered: false,
      orderDelivered: false,
      buyerReceived: false,
      ...(i?.deliveryStatus ?? {}),
    },
  }));

// ---------------------- //
// UPSERT FOOD ORDER
// ---------------------- //
export const upsertFoodOrder = createAsyncThunk<
  FoodOrder,
  Partial<FoodOrder> & { paymentId: string },
  { rejectValue: string }
>("foodOrder/upsert", async (orderData, { rejectWithValue }) => {
  try {
    if (!orderData.paymentId) throw new Error("Missing paymentId");

    const orderId = orderData.paymentId;
    const docRef = doc(db, FOOD_ORDERS_COLLECTION, orderId);

    const existingSnap = await getDoc(docRef);

    if (existingSnap.exists()) {
      /* myBrands is derived, never persisted */
      const { myBrands: _drop, ...writable } = orderData as any;
      const patch = writable.items
        ? { ...writable, items: withDeliveryDefaults(writable.items) }
        : writable;

      await updateDoc(docRef, {
        ...patch,
        updatedAt: serverTimestamp(),
      });

      const existing = existingSnap.data() as FoodOrder;
      const { id: _ignore1, ...restA } = existing;
      const { id: _ignore2, ...restB } = patch;

      return { id: orderId, ...restA, ...restB } as FoodOrder;
    }

    const payload: Omit<FoodOrder, "id"> = {
      guestId: orderData.guestId!,
      items: withDeliveryDefaults(orderData.items || []),
      subtotal: orderData.subtotal || 0,
      totalQty: orderData.totalQty || 0,
      totalPaid: orderData.totalPaid || 0,
      deliveryTotal: orderData.deliveryTotal || 0,
      guestsFee: orderData.guestsFee || 0,
      hostsFee: orderData.hostsFee || 0,
      hostProfits: orderData.hostProfits || {},
      platformProfit: orderData.platformProfit || 0,
      paymentId: orderData.paymentId,
      paymentIntentId: orderData.paymentIntentId || undefined,
      fulfillmentMethod: orderData.fulfillmentMethod || "FBS",
      deliveredBy: orderData.deliveredBy || undefined,
      customer: orderData.customer || undefined,
      isConfirmed: true,
      /* respect a webhook that already settled the charge instead of
         stamping false over it */
      isPaid: orderData.isPaid ?? false,
      isCancelled: false,
      isRefunded: false,
      isProcessedForPayout: false,
      isDelivered: false,
      deliveryStatus: {
        sellerDelivered: false,
        orderDelivered: false,
        buyerReceived: false,
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(docRef, payload, { merge: true });
    return { id: orderId, ...payload } as FoodOrder;
  } catch (err: any) {
    console.error("🔥 upsertFoodOrder error:", err);
    return rejectWithValue(err.message || "Failed to upsert food order");
  }
});

// ---------------------- //
// STATUS LIFECYCLE HELPERS
// ---------------------- //
const patchOrder = async (orderId: string, patch: Record<string, any>) => {
  const ref = doc(db, FOOD_ORDERS_COLLECTION, orderId);
  await updateDoc(ref, { ...patch, updatedAt: serverTimestamp() });
  const snap = await getDoc(ref);
  return { id: orderId, ...(snap.data() as Omit<FoodOrder, "id">) } as FoodOrder;
};

export const markFoodOrderPaid = createAsyncThunk<
  FoodOrder,
  { orderId: string; totalPaid?: number },
  { rejectValue: string }
>("foodOrder/markPaid", async ({ orderId, totalPaid }, { rejectWithValue }) => {
  try {
    const patch: Record<string, any> = { isPaid: true };
    if (typeof totalPaid === "number") patch.totalPaid = totalPaid;
    return await patchOrder(orderId, patch);
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

/** Mark every line belonging to these brands delivered — the food
    equivalent of storeOrders' markStoreItemsDelivered. Food items[]
    is flat (no brands[] wrapper), so "a brand's row" here is just the
    subset of items[] carrying that brandId; the delivery/rollup logic
    otherwise matches the store thunk exactly. */
export const markFoodItemsDelivered = createAsyncThunk<
  FoodOrder,
  { orderId: string; brandIds: string[]; role?: "seller" | "buyer" },
  { rejectValue: string }
>("foodOrder/markItemsDelivered", async (
  { orderId, brandIds, role = "seller" },
  { rejectWithValue }
) => {
  try {
    if (!brandIds?.length) throw new Error("No brand selected");

    const ref = doc(db, FOOD_ORDERS_COLLECTION, orderId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Order not found");

    const raw = snap.data() as any;
    if (!Array.isArray(raw.items)) throw new Error("Order has no items[]");

    const patch = statusPatchFor(role);
    const wanted = new Set(brandIds.map(String));

    const items = raw.items.map((i: any) =>
      wanted.has(brandKeyOf(i))
        ? { ...i, deliveryStatus: { ...(i.deliveryStatus ?? {}), ...patch } }
        : i
    );

    const deliveryStatus = rollupDelivery(items);
    return await patchOrder(orderId, {
      items,
      deliveryStatus,
      isDelivered: isFullyDelivered(deliveryStatus),
    });
  } catch (err: any) {
    console.error("🔥 markFoodItemsDelivered:", err);
    return rejectWithValue(err.message);
  }
});

/** Single line — matched on listingId, optionally narrowed by portion
    and extras so two variants of the same dish don't stamp together. */
export const markFoodItemDelivered = createAsyncThunk<
  FoodOrder,
  {
    orderId: string;
    listingId: string;
    portionId?: string | null;
    extraIds?: string[];
    role?: "seller" | "buyer";
  },
  { rejectValue: string }
>("foodOrder/markItemDelivered", async (
  { orderId, listingId, portionId, extraIds, role = "seller" },
  { rejectWithValue }
) => {
  try {
    const ref = doc(db, FOOD_ORDERS_COLLECTION, orderId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Order not found");

    const raw = snap.data() as any;
    const patch = statusPatchFor(role);
    const sameExtras = (a: any[] = [], b: any[] = []) =>
      a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|");

    const items = (raw.items ?? []).map((i: any) => {
      const hit =
        i.listingId === listingId &&
        (portionId === undefined || (i.portionId ?? null) === (portionId ?? null)) &&
        (extraIds === undefined || sameExtras(i.extraIds, extraIds));
      return hit
        ? { ...i, deliveryStatus: { ...(i.deliveryStatus ?? {}), ...patch } }
        : i;
    });

    /* matches markStoreItemDelivered: isDelivered follows orderDelivered
       alone, same as the store slice's singular-item thunk */
    const allShippedOf = (list: any[]) =>
      list.length > 0 && list.every((i: any) => i.deliveryStatus?.orderDelivered === true);

    return await patchOrder(orderId, { items, isDelivered: allShippedOf(items) });
  } catch (err: any) {
    console.error("🔥 markFoodItemDelivered:", err);
    return rejectWithValue(err.message);
  }
});

/** Payout bookkeeping — flags the order and, when hostIds are given,
    only those hosts' lines. */
export const markFoodOrderProcessedForPayout = createAsyncThunk<
  FoodOrder,
  { orderId: string; hostIds?: string[] },
  { rejectValue: string }
>("foodOrder/markProcessedForPayout", async (
  { orderId, hostIds },
  { rejectWithValue }
) => {
  try {
    const ref = doc(db, FOOD_ORDERS_COLLECTION, orderId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Order not found");

    const raw = snap.data() as any;
    const wanted = hostIds?.length ? new Set(hostIds) : null;

    const items = (raw.items ?? []).map((i: any) =>
      !wanted || wanted.has(i.hostId)
        ? { ...i, payoutProcessed: true, payoutProcessedAt: Date.now() }
        : i
    );

    const allPaidOut =
      items.length > 0 && items.every((i: any) => i.payoutProcessed === true);

    return await patchOrder(orderId, { items, isProcessedForPayout: allPaidOut });
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

export const cancelFoodOrder = createAsyncThunk<
  FoodOrder,
  string,
  { rejectValue: string }
>("foodOrder/cancel", async (orderId, { rejectWithValue }) => {
  try {
    return await patchOrder(orderId, { isCancelled: true, isConfirmed: false });
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

export const refundFoodOrder = createAsyncThunk<
  FoodOrder,
  string,
  { rejectValue: string }
>("foodOrder/refund", async (orderId, { rejectWithValue }) => {
  try {
    return await patchOrder(orderId, { isRefunded: true });
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

// ---------------------- //
// FETCH GUEST ORDERS
// ---------------------- //
export const fetchFoodGuestOrders = createAsyncThunk<
  FoodOrder[],
  string,
  { rejectValue: string }
>("foodOrder/fetchGuestOrders", async (guestId, { rejectWithValue }) => {
  try {
    const q = query(
      collection(db, FOOD_ORDERS_COLLECTION),
      where("guestId", "==", guestId)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const raw = d.data() as any;
      return {
        ...raw,
        id: d.id,
        /* buyer sees every kitchen on the order */
        myBrands: brandRowsOf(raw.items ?? []),
      } as FoodOrder;
    });
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

/* FoodOrders has no top-level hostId — one order can span several
   kitchens. The host's slice lives at hostProfits[hostId], so we query
   that map key: it exists only on orders this host is part of. This
   replaces the old `hostProfits != {}` read-everything-then-filter
   scan, which pulled the whole collection to the client and would be
   rejected outright by any sane security rule. */
const hostOrdersQuery = (hostId: string) =>
  query(
    collection(db, FOOD_ORDERS_COLLECTION),
    where(new FieldPath("hostProfits", hostId, "hostsFee"), ">=", 0)
  );

/** Only the lines this host cooks, plus their own earnings hoisted to
    the top level so the UI can read them flat, plus myBrands — the
    derived counterpart to storeOrders' brand rows. */
const scopeOrderToHost = (raw: any, id: string, hostId: string): FoodOrder => {
  const all: any[] = raw.items ?? [];
  const mine = all.filter((i) => i.hostId === hostId);
  const scoped = mine.length ? mine : all;
  const cut = raw.hostProfits?.[hostId] ?? {};
  return {
    ...raw,
    id,
    hostId,
    items: scoped,
    myBrands: brandRowsOf(scoped, mine.length ? hostId : undefined),
    hostsFee: cut.hostsFee ?? 0,
    hostProfit: cut.hostProfit ?? 0,
    deliveryFee: cut.deliveryFee ?? 0,
    saleTotal: cut.saleTotal ?? 0,
  } as FoodOrder;
};

// ---------------------- //
// FETCH HOST ORDERS
// ---------------------- //
export const fetchFoodHostOrders = createAsyncThunk<
  FoodOrder[],
  string,
  { rejectValue: string }
>("foodOrder/fetchHostOrders", async (hostId, { rejectWithValue }) => {
  try {
    const snap = await getDocs(hostOrdersQuery(hostId));
    return snap.docs.map((d) => scopeOrderToHost(d.data(), d.id, hostId));
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

// ---------------------- //
// REAL-TIME: HOST
// ---------------------- //
export const listenToFoodHostOrders =
  (hostId: string) => (dispatch: AppDispatch) => {
    if (!hostId) return;

    return onSnapshot(
      hostOrdersQuery(hostId),
      (snapshot) => {
        dispatch(
          setHostOrders(
            snapshot.docs.map((d) => scopeOrderToHost(d.data(), d.id, hostId))
          )
        );
      },
      (err) => console.error("🔥 listenToFoodHostOrders:", err)
    );
  };

/* ═══════════════════════════════════════════════════════════
   BRAND INSIGHTS — customers / analytics / finance / growth
   Orders are keyed by hostId and items[] carry only a listingId.
   FastFood/{listingId} is the bridge: it holds both brandId and
   hostId. So each listener runs in two stages — watch the brand's
   menu to learn the listingIds + owning hostId, then watch that
   host's orders and keep only the lines on that menu.
═══════════════════════════════════════════════════════════ */

/* the checkout prices portion + extras into the line rate, so trust
   item.rate when it's there and fall back to the menu rate */
const lineTotal = (listing: any, item: any) =>
  Number(item?.rate ?? listing?.rate ?? 0) * Number(item?.qty ?? 0);

const msOf = (ts: any): number | null => {
  if (!ts) return null;
  const n = Number(ts?.seconds ? ts.seconds * 1000 : ts?.toMillis?.() ?? ts);
  return Number.isFinite(n) ? n : null;
};
const pctChange = (now: number, prev: number) =>
  prev > 0 ? Number((((now - prev) / prev) * 100).toFixed(1)) : null;

interface ScopedOrder {
  id: string;
  raw: any;
  /* the host who owns this brand — carried through so finance reads
     hostProfits[thisHost] rather than whichever key Firestore
     happened to serialise first */
  hostId: string;
  items: any[];
  revenue: number;
  units: number;
  ratio: number;
  at: number | null;
}

const watchBrandFoodOrders = (
  brandId: string,
  emit: (orders: ScopedOrder[], listings: Map<string, any>) => void
) => {
  if (!brandId) return () => {};

  let listings = new Map<string, any>();
  let hostId = "";
  let unsubOrders: (() => void) | null = null;

  const stopOrders = () => {
    if (unsubOrders) {
      unsubOrders();
      unsubOrders = null;
    }
  };

  const startOrders = () => {
    stopOrders();
    if (!hostId) {
      emit([], listings);
      return;
    }

    const scopedHost = hostId;

    unsubOrders = onSnapshot(
      hostOrdersQuery(scopedHost),
      (snap) => {
        const scoped: ScopedOrder[] = [];
        snap.docs.forEach((d) => {
          const raw = d.data() as any;
          const mine = (raw.items ?? []).filter((i: any) => listings.has(i.listingId));
          if (!mine.length) return;

          let revenue = 0;
          let units = 0;
          mine.forEach((i: any) => {
            revenue += lineTotal(listings.get(i.listingId), i);
            units += Number(i.qty ?? 0);
          });

          /* hostProfits[hostId] covers the seller's whole slice of the
             order, which may span several of their brands — ratio scales
             the fees down to this brand's share */
          const hostSale = Number(raw.hostProfits?.[scopedHost]?.saleTotal ?? 0);
          scoped.push({
            id: d.id,
            raw,
            hostId: scopedHost,
            items: mine,
            revenue: Number(revenue.toFixed(2)),
            units,
            ratio: hostSale > 0 ? Math.min(1, revenue / hostSale) : 1,
            at: msOf(raw.createdAt),
          });
        });
        emit(scoped, listings);
      },
      (err) => console.error("🔥 watchBrandFoodOrders/orders:", err)
    );
  };

  const unsubMenu = onSnapshot(
    query(collection(db, FOOD_COLLECTION), where("brandId", "==", brandId)),
    (snap) => {
      listings = new Map(
        snap.docs.map((d) => [d.id, { listingId: d.id, ...(d.data() as any) }])
      );
      const nextHost = String(
        snap.docs.find((d) => (d.data() as any).hostId)?.data()?.hostId ?? ""
      );
      if (nextHost !== hostId) {
        hostId = nextHost;
        startOrders();
      } else if (!unsubOrders) startOrders();
    },
    (err) => console.error("🔥 watchBrandFoodOrders/menu:", err)
  );

  return () => {
    unsubMenu();
    stopOrders();
  };
};

/* ── 1. CUSTOMERS ── */
export const listenToFoodBrandCustomers =
  (brandId: string) => (dispatch: AppDispatch) =>
    watchBrandFoodOrders(brandId, (orders) => {
      const byGuest = new Map<string, BrandCustomer>();
      const cutoff = Date.now() - 90 * DAY;

      orders.forEach((o) => {
        if (o.raw.isCancelled || !o.raw.guestId) return;
        const c = byGuest.get(o.raw.guestId) ?? {
          guestId: o.raw.guestId,
          orders: 0,
          units: 0,
          totalSpent: 0,
          firstOrderAt: null,
          lastOrderAt: null,
          status: "lapsed" as const,
        };
        c.orders += 1;
        c.units += o.units;
        c.totalSpent = Number((c.totalSpent + o.revenue).toFixed(2));
        if (o.at !== null) {
          c.firstOrderAt = c.firstOrderAt === null ? o.at : Math.min(c.firstOrderAt, o.at);
          c.lastOrderAt = c.lastOrderAt === null ? o.at : Math.max(c.lastOrderAt, o.at);
        }
        byGuest.set(o.raw.guestId, c);
      });

      dispatch(
        setFoodBrandCustomers(
          [...byGuest.values()]
            .map((c) => ({
              ...c,
              status: (c.lastOrderAt ?? 0) >= cutoff ? ("active" as const) : ("lapsed" as const),
            }))
            .sort((a, b) => b.totalSpent - a.totalSpent)
        )
      );
    });

/* ── 2. ANALYTICS ──
   No views or conversion rate: there's no page-view collection, so
   fulfilmentRate is the real metric in its place. */
export const listenToFoodBrandAnalytics =
  (brandId: string) => (dispatch: AppDispatch) =>
    watchBrandFoodOrders(brandId, (orders, listings) => {
      const paid = orders.filter((o) => o.raw.isPaid && !o.raw.isCancelled);
      const revenue = paid.reduce((s, o) => s + o.revenue, 0);

      const per = new Map<string, TopProduct>();
      paid.forEach((o) =>
        o.items.forEach((i: any) => {
          const l = listings.get(i.listingId);
          const p = per.get(i.listingId) ?? {
            listingId: i.listingId,
            name: l?.name ?? "Untitled dish",
            photo: l?.photos?.[0],
            units: 0,
            revenue: 0,
            orders: 0,
          };
          p.units += Number(i.qty ?? 0);
          p.revenue = Number((p.revenue + lineTotal(l, i)).toFixed(2));
          p.orders += 1;
          per.set(i.listingId, p);
        })
      );

      const delivered = paid.filter((o) => o.raw.isDelivered).length;

      dispatch(
        setFoodBrandAnalytics({
          orders: paid.length,
          units: paid.reduce((s, o) => s + o.units, 0),
          revenue: Number(revenue.toFixed(2)),
          avgOrderValue: paid.length ? Number((revenue / paid.length).toFixed(2)) : 0,
          deliveredOrders: delivered,
          cancelledOrders: orders.filter((o) => o.raw.isCancelled).length,
          fulfilmentRate: paid.length
            ? Number(((delivered / paid.length) * 100).toFixed(1))
            : 0,
          topProducts: [...per.values()].sort((a, b) => b.revenue - a.revenue).slice(0, 10),
        })
      );
    });

/* ── 3. FINANCE ──
   Fees come from this brand's own host slice, scaled by ratio to this
   brand's share of that host's sale. (storeOrdersSlice still reads
   Object.keys(hostProfits)[0] here, which picks an arbitrary host on a
   multi-seller order — the same fix belongs there.) */
export const listenToFoodBrandFinance =
  (brandId: string) => (dispatch: AppDispatch) =>
    watchBrandFoodOrders(brandId, (orders) => {
      let gross = 0,
        fees = 0,
        delivery = 0,
        refunded = 0,
        pending = 0;
      const transactions: any[] = [];

      orders.forEach((o) => {
        const cut = o.raw.hostProfits?.[o.hostId] ?? {};
        const fee = Number(((cut.hostsFee ?? 0) * o.ratio).toFixed(2));
        const ship = Number(((cut.deliveryFee ?? 0) * o.ratio).toFixed(2));

        if (o.raw.isCancelled) {
          transactions.push({
            id: o.id,
            type: "Cancelled",
            at: o.at,
            amount: 0,
            status: "completed",
          });
          return;
        }
        if (o.raw.isRefunded) {
          refunded = Number((refunded + o.revenue).toFixed(2));
          transactions.push({
            id: o.id,
            type: "Refund",
            at: o.at,
            amount: -o.revenue,
            status: "completed",
          });
          return;
        }

        gross = Number((gross + o.revenue).toFixed(2));
        fees = Number((fees + fee).toFixed(2));
        delivery = Number((delivery + ship).toFixed(2));
        if (!o.raw.isDelivered)
          pending = Number((pending + (o.revenue - fee + ship)).toFixed(2));

        transactions.push({
          id: o.id,
          type: "Sale",
          at: o.at,
          amount: o.revenue,
          status: o.raw.isDelivered ? "completed" : "pending",
        });
      });

      transactions.sort((a, b) => (b.at ?? 0) - (a.at ?? 0));
      dispatch(
        setFoodBrandFinance({
          gross,
          fees,
          delivery,
          refunded,
          pending,
          net: Number((gross - fees + delivery).toFixed(2)),
          transactions,
        })
      );
    });

/* ── 4. GROWTH — last 30 days vs the 30 before ──
   Funnel and traffic-by-channel need visit tracking that isn't
   collected anywhere, so they're omitted rather than invented. */
export const listenToFoodBrandGrowth =
  (brandId: string) => (dispatch: AppDispatch) =>
    watchBrandFoodOrders(brandId, (orders) => {
      const now = Date.now();
      const winStart = now - 30 * DAY;
      const prevStart = now - 60 * DAY;

      const billable = orders.filter(
        (o) => o.raw.isPaid && !o.raw.isCancelled && o.at !== null
      );

      let revenueNow = 0,
        revenuePrev = 0,
        ordersNow = 0,
        ordersPrev = 0;
      const daily = new Map<string, { dateKey: string; revenue: number; orders: number }>();
      const firstSeen = new Map<string, number>();

      billable.forEach((o) => {
        const at = o.at as number;
        const seen = firstSeen.get(o.raw.guestId);
        if (seen === undefined || at < seen) firstSeen.set(o.raw.guestId, at);

        if (at >= winStart) {
          revenueNow += o.revenue;
          ordersNow += 1;
          const k = new Date(at).toISOString().slice(0, 10);
          const row = daily.get(k) ?? { dateKey: k, revenue: 0, orders: 0 };
          row.revenue = Number((row.revenue + o.revenue).toFixed(2));
          row.orders += 1;
          daily.set(k, row);
        } else if (at >= prevStart) {
          revenuePrev += o.revenue;
          ordersPrev += 1;
        }
      });

      const buyers = new Set(
        billable.filter((o) => (o.at as number) >= winStart).map((o) => o.raw.guestId)
      );
      let newCustomers = 0,
        returningCustomers = 0;
      buyers.forEach((g) =>
        (firstSeen.get(g) ?? 0) >= winStart ? newCustomers++ : returningCustomers++
      );

      dispatch(
        setFoodBrandGrowth({
          revenueNow: Number(revenueNow.toFixed(2)),
          revenuePrev: Number(revenuePrev.toFixed(2)),
          revenueChangePct: pctChange(revenueNow, revenuePrev),
          ordersNow,
          ordersPrev,
          ordersChangePct: pctChange(ordersNow, ordersPrev),
          newCustomers,
          returningCustomers,
          repeatRatePct: buyers.size
            ? Number(((returningCustomers / buyers.size) * 100).toFixed(1))
            : 0,
          daily: [...daily.values()].sort((a, b) => a.dateKey.localeCompare(b.dateKey)),
        })
      );
    });

/* ── 5. RAW ORDER FEED — this brand's scoped orders as-is, no rollup ── */
export const listenToFoodBrandOrders =
  (brandId: string) => (dispatch: AppDispatch) =>
    watchBrandFoodOrders(brandId, (orders) => {
      dispatch(
        setFoodBrandOrders(
          orders.map((o) => ({
            ...o.raw,
            id: o.id,
            hostId: o.hostId,
            items: o.items,
            myBrands: brandRowsOf(o.items),
            revenue: o.revenue,
            units: o.units,
          }))
        )
      );
    });

// ---------------------- //
// REAL-TIME: GUEST
// ---------------------- //
export const listenToFoodGuestOrders =
  (guestId: string) => (dispatch: AppDispatch) => {
    if (!guestId) return;

    const q = query(
      collection(db, FOOD_ORDERS_COLLECTION),
      where("guestId", "==", guestId)
    );

    return onSnapshot(
      q,
      (snapshot) => {
        dispatch(
          setGuestOrders(
            snapshot.docs.map((d) => {
              const raw = d.data() as any;
              return {
                ...raw,
                id: d.id,
                myBrands: brandRowsOf(raw.items ?? []),
              } as FoodOrder;
            })
          )
        );
      },
      (err) => console.error("🔥 listenToFoodGuestOrders:", err)
    );
  };

// ---------------------- //
// REAL-TIME: BY SESSION ID
// ---------------------- //
export const listenToFoodOrderBySessionId =
  (sessionId: string, userId: string) => (dispatch: AppDispatch) => {
    if (!sessionId || !userId) return;

    const ref = doc(db, FOOD_ORDERS_COLLECTION, sessionId);

    return onSnapshot(ref, (docSnap) => {
      if (!docSnap.exists()) {
        dispatch(setOrderById(null));
        return;
      }
      const data = docSnap.data() as Omit<FoodOrder, "id">;

      if (data.guestId !== userId) {
        dispatch(setOrderById(null));
        return;
      }

      dispatch(
        setOrderById({
          ...data,
          id: docSnap.id,
          myBrands: brandRowsOf(data.items ?? []),
        } as FoodOrder)
      );
    });
  };

// ---------------------- //
// REAL-TIME: SINGLE ORDER
// ---------------------- //
export const listenToFoodOrderById =
  (orderId: string) => (dispatch: AppDispatch) => {
    if (!orderId) return;

    const ref = doc(db, FOOD_ORDERS_COLLECTION, orderId);

    return onSnapshot(ref, (docSnap) => {
      if (!docSnap.exists()) {
        dispatch(setOrderById(null));
        return;
      }
      const data = docSnap.data() as Omit<FoodOrder, "id">;
      dispatch(
        setOrderById({
          ...data,
          id: docSnap.id,
          myBrands: brandRowsOf(data.items ?? []),
        } as FoodOrder)
      );
    });
  };

// ---------------------- //
// SLICE
// ---------------------- //
const foodOrderSlice = createSlice({
  name: "FoodOrder",
  initialState: {
    guestOrders: [] as FoodOrder[],
    hostOrders: [] as FoodOrder[],
    orderById: null as FoodOrder | null,
    loading: false,
    error: null as string | null,

    /* brand insights */
    brandCustomers: [] as BrandCustomer[],
    brandAnalytics: {
      orders: 0,
      units: 0,
      revenue: 0,
      avgOrderValue: 0,
      deliveredOrders: 0,
      cancelledOrders: 0,
      fulfilmentRate: 0,
      topProducts: [] as TopProduct[],
    },
    brandFinance: {
      gross: 0,
      fees: 0,
      delivery: 0,
      net: 0,
      refunded: 0,
      pending: 0,
      transactions: [] as any[],
    },
    brandGrowth: {
      revenueNow: 0,
      revenuePrev: 0,
      revenueChangePct: null as number | null,
      ordersNow: 0,
      ordersPrev: 0,
      ordersChangePct: null as number | null,
      newCustomers: 0,
      returningCustomers: 0,
      repeatRatePct: 0,
      daily: [] as { dateKey: string; revenue: number; orders: number }[],
    },
    brandOrders: [] as any[],
  },
  reducers: {
    setGuestOrders: (state, action: PayloadAction<FoodOrder[]>) => {
      state.guestOrders = action.payload;
    },
    setHostOrders: (state, action: PayloadAction<FoodOrder[]>) => {
      state.hostOrders = action.payload;
    },
    setOrderById: (state, action: PayloadAction<FoodOrder | null>) => {
      state.orderById = action.payload;
    },
    clearOrderById: (state) => {
      state.orderById = null;
    },
    updateOrderDeliveryStatus: (
      state,
      action: PayloadAction<{
        orderId: string;
        itemIndex: number;
        status: Partial<FoodDeliveryStatus>;
      }>
    ) => {
      const { orderId, itemIndex, status } = action.payload;

      const stamp = (order?: FoodOrder | null) => {
        if (!order?.items?.[itemIndex]) return;
        order.items[itemIndex].deliveryStatus = {
          ...order.items[itemIndex].deliveryStatus,
          ...status,
        };
        /* keep the derived rows honest after an optimistic stamp */
        order.myBrands = brandRowsOf(order.items);
      };

      stamp(state.guestOrders.find((o) => o.id === orderId));
      stamp(state.hostOrders.find((o) => o.id === orderId));
      if (state.orderById?.id === orderId) stamp(state.orderById);
    },
    setFoodBrandCustomers: (s, a: PayloadAction<BrandCustomer[]>) => {
      s.brandCustomers = a.payload;
    },
    setFoodBrandAnalytics: (s, a: PayloadAction<any>) => {
      s.brandAnalytics = a.payload;
    },
    setFoodBrandFinance: (s, a: PayloadAction<any>) => {
      s.brandFinance = a.payload;
    },
    setFoodBrandGrowth: (s, a: PayloadAction<any>) => {
      s.brandGrowth = a.payload;
    },
    setFoodBrandOrders: (s, a: PayloadAction<any[]>) => {
      s.brandOrders = a.payload;
    },
  },
  extraReducers: (builder) => {
    /* keep any order that comes back from a transition thunk in sync
       across all three buckets it might live in, preserving whatever
       host scoping that bucket already had */
    const syncOrder = (state: any, order: FoodOrder) => {
      const merge = (prev: FoodOrder): FoodOrder => {
        const scopedHost = (prev as any).hostId;
        const all: any[] = order.items ?? [];
        const mine = scopedHost ? all.filter((i) => i.hostId === scopedHost) : all;
        const items = mine.length ? mine : all;
        return {
          ...prev,
          ...order,
          items,
          myBrands: brandRowsOf(items, mine.length ? scopedHost : undefined),
        };
      };

      const gi = state.guestOrders.findIndex((o: FoodOrder) => o.id === order.id);
      if (gi >= 0) state.guestOrders[gi] = merge(state.guestOrders[gi]);

      const hi = state.hostOrders.findIndex((o: FoodOrder) => o.id === order.id);
      if (hi >= 0) state.hostOrders[hi] = merge(state.hostOrders[hi]);

      if (state.orderById?.id === order.id) state.orderById = merge(state.orderById);
    };

    builder
      .addCase(upsertFoodOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(upsertFoodOrder.fulfilled, (state, action) => {
        const withRows = {
          ...action.payload,
          myBrands: brandRowsOf(action.payload.items ?? []),
        };
        const idx = state.guestOrders.findIndex((o) => o.id === withRows.id);
        if (idx >= 0) state.guestOrders[idx] = withRows;
        else state.guestOrders.push(withRows);
        state.loading = false;
      })
      .addCase(upsertFoodOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to upsert food order";
      })

      .addCase(fetchFoodGuestOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFoodGuestOrders.fulfilled, (state, action) => {
        state.guestOrders = action.payload;
        state.loading = false;
      })
      .addCase(fetchFoodGuestOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch guest food orders";
      })

      .addCase(fetchFoodHostOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFoodHostOrders.fulfilled, (state, action) => {
        state.hostOrders = action.payload;
        state.loading = false;
      })
      .addCase(fetchFoodHostOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch host food orders";
      })

      /* lifecycle transitions */
      .addCase(markFoodOrderPaid.fulfilled, (s, a) => syncOrder(s, a.payload))
      .addCase(markFoodItemDelivered.fulfilled, (s, a) => syncOrder(s, a.payload))
      .addCase(markFoodItemsDelivered.fulfilled, (s, a) => syncOrder(s, a.payload))
      .addCase(markFoodOrderProcessedForPayout.fulfilled, (s, a) => syncOrder(s, a.payload))
      .addCase(cancelFoodOrder.fulfilled, (s, a) => syncOrder(s, a.payload))
      .addCase(refundFoodOrder.fulfilled, (s, a) => syncOrder(s, a.payload));
  },
});

export const {
  setGuestOrders,
  setHostOrders,
  setOrderById,
  clearOrderById,
  updateOrderDeliveryStatus,
  setFoodBrandCustomers,
  setFoodBrandAnalytics,
  setFoodBrandFinance,
  setFoodBrandGrowth,
  setFoodBrandOrders,
} = foodOrderSlice.actions;

export default foodOrderSlice.reducer;